import type { Client, MatrixMediaBytes } from "~/lib/matrix";
import type { AuthenticatedMediaThumbnail } from "~/hooks/useAuthenticatedMedia";

type AvatarReadiness = "loaded" | "failed";

interface Entry {
  promise: Promise<MatrixMediaBytes>;
  media?: MatrixMediaBytes;
  url?: string;
  ready?: AvatarReadiness;
  readiness: Promise<AvatarReadiness>;
  settleReadiness: (readiness: AvatarReadiness) => void;
}

const caches = new WeakMap<Client, Map<string, Entry>>();
const LIMIT = 128;
const MAX_BYTES = 256 * 1024;

function keyFor(uri: string, thumbnail: AuthenticatedMediaThumbnail | undefined): string {
  return `${uri}|${thumbnail ? `${thumbnail.w}x${thumbnail.h}-${thumbnail.method}` : "file"}`;
}

function createSharedUrl(bytes: Uint8Array): string {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return URL.createObjectURL(new Blob([buffer], { type: "application/octet-stream" }));
}

function decodeSharedUrl(url: string): Promise<AvatarReadiness> {
  if (typeof Image === "undefined") return Promise.resolve("failed");
  return new Promise((resolve) => {
    const img = new Image();
    const finish = (result: AvatarReadiness) => {
      img.onload = null;
      img.onerror = null;
      resolve(result);
    };
    img.onload = () => finish(img.naturalWidth > 0 ? "loaded" : "failed");
    img.onerror = () => finish("failed");
    img.src = url;
  });
}

let decodeReadiness: (url: string) => Promise<AvatarReadiness> = decodeSharedUrl;
export function __setAvatarReadiness(impl: (url: string) => Promise<AvatarReadiness>): void {
  decodeReadiness = impl;
}
export function __resetAvatarReadiness(): void {
  decodeReadiness = decodeSharedUrl;
}

function makeReadiness(): {
  promise: Promise<AvatarReadiness>;
  settle: (readiness: AvatarReadiness) => void;
} {
  let settled = false;
  let resolve: ((readiness: AvatarReadiness) => void) | null = null;
  const promise = new Promise<AvatarReadiness>((done) => {
    resolve = (readiness) => {
      if (settled) return;
      settled = true;
      done(readiness);
    };
  });
  return { promise, settle: (readiness) => resolve?.(readiness) };
}

function drop(cache: Map<string, Entry>, key: string): void {
  const removed = cache.get(key);
  if (!removed) return;
  cache.delete(key);
  if (removed.url) URL.revokeObjectURL(removed.url);
  removed.settleReadiness("failed");
}

function evictLru(cache: Map<string, Entry>): void {
  while (cache.size > LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    drop(cache, oldest);
  }
}

function clearAvatarMedia(client: Client): void {
  const cache = caches.get(client);
  if (!cache) return;
  for (const entry of cache.values()) {
    if (entry.url) URL.revokeObjectURL(entry.url);
    entry.settleReadiness("failed");
  }
  cache.clear();
}

export { clearAvatarMedia };

export function avatarMedia(client: Client, uri: string, thumbnail?: AuthenticatedMediaThumbnail) {
  let cache = caches.get(client);
  if (!cache) {
    cache = new Map();
    caches.set(client, cache);
  }
  const key = keyFor(uri, thumbnail);
  const existing = cache.get(key);
  if (existing) {
    cache.delete(key);
    cache.set(key, existing);
    return existing;
  }
  const readiness = makeReadiness();
  const entry: Entry = {
    promise: client.bindings.downloadMedia({ url: uri }, thumbnail ? { thumbnail } : undefined),
    readiness: readiness.promise,
    settleReadiness: (result) => {
      if (entry.ready !== undefined) return;
      entry.ready = result;
      readiness.settle(result);
    },
  };
  cache.set(key, entry);
  void entry.promise.then(
    (media) => {
      if (cache.get(key) !== entry) {
        entry.media = media;
        return;
      }
      if (media.bytes.byteLength > MAX_BYTES) {
        entry.media = media;
        drop(cache, key);
        return;
      }
      entry.media = media;
      entry.url = createSharedUrl(media.bytes);
      void decodeReadiness(entry.url).then((result) => entry.settleReadiness(result));
    },
    () => {
      entry.settleReadiness("failed");
      if (cache.get(key) === entry) drop(cache, key);
    },
  );
  evictLru(cache);
  return entry;
}

export function cachedAvatarUrl(
  client: Client,
  uri: string,
  thumbnail?: AuthenticatedMediaThumbnail,
): string | null {
  return caches.get(client)?.get(keyFor(uri, thumbnail))?.url ?? null;
}

export function cachedReadyAvatarUrl(
  client: Client,
  uri: string,
  thumbnail?: AuthenticatedMediaThumbnail,
): string | null {
  const entry = caches.get(client)?.get(keyFor(uri, thumbnail));
  return entry?.ready === "loaded" ? (entry.url ?? null) : null;
}

export async function preloadAvatars(
  client: Client,
  uris: string[],
  thumbnail?: AuthenticatedMediaThumbnail,
): Promise<void> {
  const unique = [...new Set(uris)];
  await Promise.allSettled(
    unique.map((uri) =>
      Promise.resolve().then(() => avatarMedia(client, uri, thumbnail).readiness),
    ),
  );
}
